import PageLayout from "components/layout/PageLayout";
import { useGetMyHealthPatientDetailQuery, useUploadHealthDataMutation, useUploadHealthEventsMutation } from "./api/HealthDataApi";
import { useTranslation } from "react-i18next";
import { HealthPatientCard } from "../history/views/HealthPatientCard";
import { useLazyGetHealthPatientDetailQuery } from "../patients/api/healthPatientsApiSlice";
import { selectCompanyGuid, selectCurrentUserGuid } from "features/auth/authSlice";
import { useSelector } from "react-redux";
import { useState } from "react";
import { useWebSocket } from "app/ws/WebSocketProvider";


export default function HealthDebugTool() {

    const [t] = useTranslation(["global", "health"])

    const { data: myPatientDetail, isFetching: patientIsFetching, isSuccess: patientIsSuccess, refetch: refetchMyPatient } = useGetMyHealthPatientDetailQuery()

    const { connectionStatus: wsStatus, client: wsClient } = useWebSocket()
    const companyGuid = useSelector(selectCompanyGuid)
    const userId = useSelector(selectCurrentUserGuid)
    const topic = `company/${companyGuid}/health/${userId}`

    const [realtTimeHR, setRealTimeHR] = useState(80)

    const [uploadData, { isLoading: dataIsUploading }] = useUploadHealthDataMutation()
    const [uploadEvents, { isLoading: eventsIsUploading }] = useUploadHealthEventsMutation()

    const availableEventTypes =
        [
            // critic events
            "FALL_DETECT", "NO_ACTIVITY", "HIGH_HR", "LOW_HR",
            // measurements events
            "BIA", "ECG", "SPO2"
        ]
    const availableDataTypes = ["HR", "SP02", "STEPS_DAILY", "WRIST_SKIN_TEMP", "AMBIENT_TEMP", "VO2_MAX"]


    function getEventTranslation(type) {
        return t(`health:entity.events.${type}`)
    }


    function getDataUnit(type) {
        switch (type) {
            case "HR": return "bpm"
            case "SP02": return "%"
            case "STEPS_DAILY": return "steps"
            case "WRIST_SKIN_TEMP":
            case "AMBIENT_TEMP": return "°C"
            case "VO2_MAX": return "vo2Max"
            default: return null
        }
    }

    // if getEventUnit returns null means that event doesn't need a value in the input form
    function getEventUnit(type) {
        switch (type) {
            case "FALL_DETECT": return "m/s^2"
            case "HIGH_HR":
            case "LOW_HR": return "bpm"
            case "BIA": return "%"
            case "SPO2": return "%"
            default: return null
        }
    }

    console.log("realtTimeHR: " + realtTimeHR)


    const handleStatusSending = () => {

        let currentEpochSeconds = Math.round(new Date().getTime() / 1000)

        const message = {
            hr: realtTimeHR,
            epochTime: currentEpochSeconds
        }
        try {
            console.log("Publishing on topic " + topic)
            console.log("Payload " + JSON.stringify(message))
            wsClient.publish(topic, JSON.stringify(message))
        } catch (err) {
            console.error(`Error trying to publish ${message.toString()} on topic ${topic}`)
        }



        if (patientIsSuccess) {
            let hrSafeZoneFeature = myPatientDetail.profile?.features.find((feature) => feature.name === "HR-SAFEZONE")
            let hrTracking = myPatientDetail.profile?.features.find((feature) => feature.name === "HR")

            // Check if HR tracking is enabled, so upload data
            if (hrTracking.enabled) {
                uploadData([{
                    type: "HR",
                    value: realtTimeHR,
                    timestamp: currentEpochSeconds
                }])
            }

            // Check if it's critic event, so upload event
            let eventType = null
            if (hrSafeZoneFeature.enabled && realtTimeHR > hrSafeZoneFeature.settings.max) {
                eventType = "HIGH_HR"
            } else if (hrSafeZoneFeature.enabled && realtTimeHR < hrSafeZoneFeature.settings.min) {
                eventType = "LOW_HR"
            }
            if (eventType != null) {
                uploadEvents([{
                    type: eventType,
                    value: realtTimeHR,
                    timestamp: currentEpochSeconds
                }])
            }
        }

    }

    let content = <div className="col">
        {
            patientIsSuccess &&
            <HealthPatientCard patient={myPatientDetail} />
        }

        <div className="row mt-2">
            <div className="col-auto">
                <div className="card bg-dark">
                    <div className="card-header">Real Time Health Status</div>
                    <div className="card-body d-flex justify-content-between">
                        <input type="number" id="realtime-hr" className="w-50" value={realtTimeHR} onChange={(e) => setRealTimeHR(parseInt(e.target.value))}></input> {getDataUnit("HR")}
                        <button className="btn btn-primary" onClick={handleStatusSending}>Invia</button>
                    </div>

                </div>
            </div>

        </div>



    </div>


    return <PageLayout
        pageTitle="Health Simulation Tool"
        onPageRefresh={refetchMyPatient}
        isLoadingContent={dataIsUploading || eventsIsUploading || patientIsFetching}
        bodyContent={content}
    />

}

let exampleDataUploadBatch = [
    {
        "type": "HR",
        "value": "131",
        "timestamp": 1763751385
    },
    {
        "type": "SP02",
        "value": "95",
        "timestamp": 1763751485
    },
    {
        "type": "HR",
        "value": "73",
        "timestamp": 1763751585
    }
]

let exampleEventsUploadBatch = [
    {
        "type": "ECG",
        "value": null, // no value, it's only an measurement event
        "timestamp": 1763711300
    },
    {
        "type": "FALL_DETECT",
        "value": "22", // 22 m/s^2 falling acceleration
        "timestamp": 1763711485
    },
    {
        "type": "HIGH_HR",
        "value": "131",  // bpm detected exceeding HR-SAFEZONE threshold (131 > 120) (if feature is enabled on patient)
        "timestamp": 1763751385
    },
]




let myPatientExample = {
    "guid": "b2877d94-51c0-40ea-81f7-bd067b72b072",
    "user": {
        "guid": "e1c43cba-3c64-4ab6-95b0-804700e16230",
        "displayName": "Mario Ruberini",
        "email": "mario.worker@mitfen.com",
        "title": null
    },
    "profile": {
        "guid": "a878e73e-4bd0-46f3-aa6c-06f0696d6707",
        "isDefault": true,
        "name": "(default)",
        "features": [
            {
                "name": "HR-SAFEZONE",
                "enabled": true,
                "settings": {
                    "min": 40.0,
                    "max": 120.0
                }
            },
            {
                "name": "FALL-DETECTION",
                "enabled": true,
                "settings": {
                    "threshold": 20.0
                }
            },
            {
                "name": "INACTIVITY",
                "enabled": true,
                "settings": {
                    "threshold": 0.1
                }
            },
            {
                "name": "HR",
                "enabled": true,
                "settings": null
            },
            {
                "name": "STEPS",
                "enabled": true,
                "settings": null
            },
            {
                "name": "TEMPS",
                "enabled": true,
                "settings": null
            },
            {
                "name": "VO2MAX",
                "enabled": true,
                "settings": null
            }
        ],
        "emergencyNumber": "",
        "syncPeriod": 30
    },
    "info": {
        "weight": 79.0,
        "height": 170.0,
        "birthDate": -420422400, // Unix epoch seconds,  79 years old in 2025
        "gender": "MALE"
    }
}